import React, { useEffect, useRef, useState } from 'react';
import { Button, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import { cleanTableName, tableNameError } from './tableList.utils';
import tableNameErrorText from './tableNameErrorText';

/**
 * Rename a table in its row: Save or Enter keeps it, Cancel or Esc drops it. The link and printed
 * QR codes stay the same; orders already placed keep the name they were placed with.
 */
export default function RenameTableForm({ table, tables, onRename, onDone }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Takeout:Tables:TableList' });
  const [value, setValue] = useState(table.name);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);
  const hintId = `table-rename-hint-${table.id}`;

  useEffect(() => {
    inputRef.current?.focus({ cursor: 'all' });
  }, []);

  const submit = async (event) => {
    event.preventDefault();

    if (saving) {
      return;
    }

    const problem = tableNameErrorText(t, tableNameError(value, tables, table));

    if (problem) {
      setError(problem);

      return;
    }

    if (cleanTableName(value) === table.name) {
      onDone();

      return;
    }

    setSaving(true);
    const serverError = await onRename(table, cleanTableName(value));
    setSaving(false);

    if (serverError) {
      setError(serverError);

      return;
    }

    onDone();
  };

  return (
    <form onSubmit={submit} noValidate data-testid="table-rename-form">
      <div className="flex flex-wrap items-center gap-xs">
        <Input
          ref={inputRef}
          size="small"
          className="min-w-[160px] flex-1 max-w-[240px]"
          value={value}
          autoComplete="off"
          aria-label={t('aria.new_name', { name: table.name })}
          aria-describedby={hintId}
          aria-invalid={error ? 'true' : undefined}
          status={error ? 'error' : undefined}
          onChange={(event) => {
            setValue(event.target.value);
            setError(null);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape' && !saving) {
              event.preventDefault();
              onDone();
            }
          }}
        />
        <Button size="small" type="primary" htmlType="submit" loading={saving}>
          {t('buttons.save')}
        </Button>
        <Button size="small" type="text" onClick={onDone} disabled={saving}>
          {t('buttons.cancel')}
        </Button>
      </div>
      <div id={hintId} className={error ? 'mt-2xs text-sm text-brand-danger' : 'mt-2xs text-xs text-gray-500'}>
        {error ?? t('rename_hint')}
      </div>
    </form>
  );
}
